import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Rating,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Avatar,
} from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import SectionContainer from '../components/SectionContainer';
import ImageGallery from '../components/ImageGallery';
import { services, reviews } from '../data/mockData';
import { useLanguage } from '../i18n/LanguageContext';

const Services = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const handleRequestQuote = (serviceId) => {
    navigate('/contact', { state: { selectedService: serviceId } });
  };

  const getServiceReviews = (serviceId) => {
    return reviews.filter(review => review.serviceId === serviceId || review.serviceId === null);
  };

  return (
    <Box>
      <SectionContainer backgroundColor="grey.50" py={6}>
        <Typography variant="h1" textAlign="center" gutterBottom sx={{ mb: 2 }}>
          {t('services.title')}
        </Typography>
        <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
          {t('services.subtitle')}
        </Typography>
      </SectionContainer>

      <SectionContainer>
        <Grid container spacing={6}>
          {services.map((service) => {
            const serviceReviews = getServiceReviews(service.id);
            const title = t(`servicesData.${service.key}.title`);
            const description = t(`servicesData.${service.key}.description`);
            const bullets = t(`servicesData.${service.key}.bullets`);

            return (
              <Grid item xs={12} key={service.id}>
                <Card elevation={3} sx={{ overflow: 'visible', position: 'relative' }}>
                  <CardContent sx={{ p: 4 }}>
                    <Grid container spacing={4}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                          {title}
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                          <Rating value={service.avgRating} readOnly />
                          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                            {service.avgRating} ({service.reviewCount} {t('services.reviewsSuffix')})
                          </Typography>
                        </Box>

                        <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                          {description}
                        </Typography>

                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                          {t('services.whatsIncluded')}
                        </Typography>
                        <List dense>
                          {(Array.isArray(bullets) ? bullets : []).map((bullet, index) => (
                            <ListItem key={index} sx={{ py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <CheckIcon color="primary" fontSize="small" />
                              </ListItemIcon>
                              <ListItemText primary={bullet} />
                            </ListItem>
                          ))}
                        </List>

                        <Button
                          variant="contained"
                          color="primary"
                          size="large"
                          onClick={() => handleRequestQuote(service.id)}
                          sx={{ mt: 3, px: 4 }}
                        >
                          {t('services.requestQuoteFor')} {title}
                        </Button>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                          {t('services.projectGallery')}
                        </Typography>
                        <Box sx={{ mb: 4 }}>
                          <ImageGallery images={service.imageUrls} alt={title} />
                        </Box>

                        {serviceReviews.length > 0 && (
                          <>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                              {t('services.recentReviews')}
                            </Typography>
                            <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                              {serviceReviews.slice(0, 3).map((review) => (
                                <Paper key={review.id} elevation={1} sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Avatar sx={{ width: 32, height: 32, mr: 1, fontSize: '0.875rem' }}>
                                      {review.name.charAt(0)}
                                    </Avatar>
                                    <Box>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                        {review.name}
                                      </Typography>
                                      <Rating value={review.rating} readOnly size="small" />
                                    </Box>
                                  </Box>
                                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                    "{t(`reviewsData.${review.key}`)}"
                                  </Typography>
                                </Paper>
                              ))}
                            </Box>
                          </>
                        )}
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </SectionContainer>

      <SectionContainer backgroundColor="primary.main" sx={{ color: 'white' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 600 }}>
            {t('services.customTitle')}
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            {t('services.customSubtitle')}
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            onClick={() => navigate('/contact')}
            sx={{ px: 4, py: 2, fontSize: '1.1rem' }}
          >
            {t('services.customButton')}
          </Button>
        </Box>
      </SectionContainer>
    </Box>
  );
};

export default Services;
